/**
 * HelixScoreViz3D.tsx — Three.js Helix (iframe wrapper)
 *
 * Mounts the production Three.js helix from /helix/index.html via iframe.
 * Matches the website's "DNA of the Deal" visualization exactly:
 *   - Three.js 0.160 with bloom postprocessing
 *   - OrbitControls (drag to rotate, scroll to zoom)
 *   - Click any checkpoint orb → emits cp-selected postMessage to parent
 *   - 10 checkpoint spine with red HARD-KILL endpoints
 *   - Dark space-like background (#0A0A0C)
 *
 * Props:
 *   height          — pixel/CSS height of the iframe (default '600px')
 *   disablePaywall  — when true, all 60+ question orbs are clickable (AIP default)
 *   autoRotate      — enable initial auto-rotation (default true)
 *   iframeRef       — optional ref forwarded to the iframe element
 *   onLoad          — optional callback when iframe loads
 */

import { useMemo, type RefObject } from "react";

interface Props {
  height?: string;
  disablePaywall?: boolean;
  autoRotate?: boolean;
  className?: string;
  iframeRef?: RefObject<HTMLIFrameElement>;
  onLoad?: () => void;
}

export default function HelixScoreViz3D({
  height = "600px",
  disablePaywall = true,
  autoRotate = true,
  className = "",
  iframeRef,
  onLoad,
}: Props) {
  const src = useMemo(() => {
    const params = new URLSearchParams();
    if (disablePaywall) params.set("paywall", "off");
    if (!autoRotate) params.set("rotate", "off");
    const qs = params.toString();
    return `/helix/index.html${qs ? `?${qs}` : ""}`;
  }, [disablePaywall, autoRotate]);

  return (
    <div
      className={`helix-3d-frame ${className}`}
      style={{
        width: "100%",
        height,
        background: "#0A0A0C",
        borderRadius: 12,
        overflow: "hidden",
        border: "1px solid rgba(255,255,255,0.08)",
      }}
    >
      <iframe
        ref={iframeRef}
        src={src}
        title="DNA of the Deal — 3D Helix"
        style={{
          width: "100%",
          height: "100%",
          border: 0,
          display: "block",
        }}
        allow="fullscreen"
        onLoad={onLoad}
      />
    </div>
  );
}
